import { TrackedEvents, trackEvent } from 'analytics';
import type { MaterialCreationProcess, MaterialOrigin } from 'api';
import { useNavigate } from 'react-router-dom';
import { DataCard, ExpandableIconButton, Grid, useBreakpoint } from 'ui';
import { CancelOutlined, CheckCircleOutline, ImageOutlined, Inventory2Outlined } from 'ui/icons';

import { formatBaleDensity } from '../../utils/formatBaleDensity/formatBaleDensity';
import { formatBaleDimensions } from '../../utils/formatBaleDimensions/formatBaleDimensions';
import {
  formatMaterialCreationProcess,
  formatMaterialOrigin,
} from '../../utils/formatMaterialSpecifications/formatMaterialSpecifications';

const SpecificationAnalysisCheck = ({ value }: { value: boolean }) => {
  return value ? (
    <Grid item alignItems="center" gap={1} display="flex">
      <CheckCircleOutline color="success" sx={{ height: '16px', width: '16px' }} />
      Completed
    </Grid>
  ) : (
    <Grid item alignItems="center" gap={1} display="flex">
      <CancelOutlined color="error" sx={{ height: '16px', width: '16px' }} />
      Not Completed
    </Grid>
  );
};

export const MaterialSpecificationsDataCard = ({
  isLoading,
  storageType,
  materialStrapping,
  height,
  width,
  length,
  weight,
  visualAnalysis,
  radiationAnalysis,
  ferrousAnalysis,
  materialAnalysisNotes,
  materialOrigin,
  materialProcess,
  hasCapsAndLabels,
  isFoodGrade,
  hasMedia,
}: {
  isLoading?: boolean;
  storageType?: string | null;
  materialStrapping?: string | null;
  height?: number | null;
  width?: number | null;
  length?: number | null;
  weight?: number | null;
  visualAnalysis?: boolean | null;
  radiationAnalysis?: boolean | null;
  ferrousAnalysis?: boolean | null;
  materialAnalysisNotes?: string | null;
  materialOrigin?: MaterialOrigin['type'] | null;
  materialProcess?: MaterialCreationProcess | null;
  hasCapsAndLabels?: boolean | null;
  isFoodGrade?: boolean | null;
  hasMedia: boolean;
}) => {
  const navigate = useNavigate();

  const handleClickMedia = () => {
    trackEvent(TrackedEvents.QUALITY_REPORT_CLICK_CATEGORY_IMAGES, {
      category: 'Material Specifications',
    });
    navigate('media?tab=specification');
  };

  const { isMobile } = useBreakpoint();
  const dimensions = formatBaleDimensions({
    height: height,
    length: length,
    width: width,
  });

  const density = formatBaleDensity({
    height: height,
    length: length,
    roundTo: 2,
    weight: weight,
    width: width,
  });

  return (
    <DataCard
      isLoading={isLoading}
      slotProps={{
        header: {
          action: hasMedia ? (
            <ExpandableIconButton icon={<ImageOutlined />} label="Show media" onClick={handleClickMedia} />
          ) : undefined,
          avatar: <Inventory2Outlined />,
          title: 'Material Specifications',
        },
      }}
    >
      {storageType ? (
        <DataCard.Item title="Material Form" colSpan={isMobile ? 6 : 2}>
          {storageType}
        </DataCard.Item>
      ) : null}
      {materialStrapping ? (
        <DataCard.Item title="Strapping" colSpan={isMobile ? 6 : 2}>
          {materialStrapping}
        </DataCard.Item>
      ) : null}
      {dimensions ? (
        <DataCard.Item title="Bale Dimensions" colSpan={isMobile ? 6 : 2}>
          {dimensions} m
        </DataCard.Item>
      ) : null}
      {density ? (
        <DataCard.Item title="Density (kg/m³)" colSpan={isMobile ? 12 : 6}>
          {density}
        </DataCard.Item>
      ) : null}
      {materialOrigin ? (
        <DataCard.Item title="Origin" colSpan={isMobile ? 6 : 2}>
          {formatMaterialOrigin(materialOrigin)}
        </DataCard.Item>
      ) : null}
      {materialProcess ? (
        <DataCard.Item title="Material Process" colSpan={isMobile ? 6 : 2}>
          {formatMaterialCreationProcess(materialProcess)}
        </DataCard.Item>
      ) : null}

      {hasCapsAndLabels != null && (
        <DataCard.Item title="Caps and Labels" colSpan={isMobile ? 6 : 2}>
          {hasCapsAndLabels ? 'Yes' : 'No'}
        </DataCard.Item>
      )}

      {isFoodGrade != null && (
        <DataCard.Item title="Food Grade" colSpan={isMobile ? 6 : 2}>
          {isFoodGrade ? 'Yes' : 'No'}
        </DataCard.Item>
      )}

      {visualAnalysis != null && (
        <DataCard.Item title="Visual Check" colSpan={isMobile ? 6 : 2}>
          <div>
            <SpecificationAnalysisCheck value={visualAnalysis} />
          </div>
        </DataCard.Item>
      )}
      {radiationAnalysis != null && (
        <DataCard.Item title="Radiation Check" colSpan={isMobile ? 6 : 2}>
          <div>
            <SpecificationAnalysisCheck value={radiationAnalysis} />
          </div>
        </DataCard.Item>
      )}
      {ferrousAnalysis != null && (
        <DataCard.Item title="Ferrous Check" colSpan={isMobile ? 6 : 2}>
          <div>
            <SpecificationAnalysisCheck value={ferrousAnalysis} />
          </div>
        </DataCard.Item>
      )}
      {materialAnalysisNotes ? (
        <DataCard.Item title="Material Analysis Notes" colSpan={12}>
          {materialAnalysisNotes}
        </DataCard.Item>
      ) : null}
    </DataCard>
  );
};
